import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Col,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined, ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createLotteryPrize,
  deleteLotteryPrize,
  getLotteryCampaignById,
  getLotteryPrizes,
  getLotteryRecords,
  updateLotteryCampaign,
  updateLotteryPrize,
  updateLotteryRecordStatus,
} from '../../api/lottery';
import type { LotteryCampaign, LotteryPrize, LotteryRecord } from '../../types/lottery';
import FulfillModal from './FulfillModal';
import PrizeModal from './PrizeModal';
import { createPrizeColumns, createRecordColumns } from './detailColumns';
import { CampaignFormValues, PrizeFormValues, RecordFilterValues } from './detailTypes';
import {
  formatCampaignTime,
  getRuntimeStatus,
  getStats,
  toCampaignFormValues,
  toCampaignPayload,
} from './detailUtils';
import './index.css';

const { Title } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const LotteryDetailPage: React.FC = () => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const params = useParams();
  const campaignId = Number(params.id);
  const hasValidCampaignId = Number.isInteger(campaignId) && campaignId > 0;
  const [campaign, setCampaign] = useState<LotteryCampaign | null>(null);
  const [prizes, setPrizes] = useState<LotteryPrize[]>([]);
  const [records, setRecords] = useState<LotteryRecord[]>([]);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsPageSize, setRecordsPageSize] = useState(20);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [prizeLoading, setPrizeLoading] = useState(false);
  const [recordLoading, setRecordLoading] = useState(false);
  const [prizeModalVisible, setPrizeModalVisible] = useState(false);
  const [editingPrize, setEditingPrize] = useState<LotteryPrize | null>(null);
  const [fulfillModalVisible, setFulfillModalVisible] = useState(false);
  const [fulfillingRecord, setFulfillingRecord] = useState<LotteryRecord | null>(null);
  const [recordFilters, setRecordFilters] = useState<RecordFilterValues>({});
  const [draftFilters, setDraftFilters] = useState<RecordFilterValues>({});
  const [campaignForm] = Form.useForm<CampaignFormValues>();
  const [prizeForm] = Form.useForm<PrizeFormValues>();
  const [fulfillForm] = Form.useForm<{ fulfillmentNote?: string }>();

  const fetchCampaign = useCallback(async () => {
    if (!hasValidCampaignId) {
      message.error('活动 ID 无效');
      navigate('/lottery');
      return;
    }

    try {
      setCampaignLoading(true);
      const data = await getLotteryCampaignById(campaignId);
      setCampaign(data);
      campaignForm.setFieldsValue(toCampaignFormValues(data));
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取活动详情失败');
    } finally {
      setCampaignLoading(false);
    }
  }, [campaignForm, campaignId, hasValidCampaignId, message, navigate]);

  const fetchPrizes = useCallback(async () => {
    if (!hasValidCampaignId) return;

    try {
      setPrizeLoading(true);
      const data = await getLotteryPrizes(campaignId);
      setPrizes(data || []);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取奖品配置失败');
    } finally {
      setPrizeLoading(false);
    }
  }, [campaignId, hasValidCampaignId, message]);

  const fetchRecords = useCallback(async () => {
    if (!hasValidCampaignId) return;

    try {
      setRecordLoading(true);
      const data = await getLotteryRecords({
        ...recordFilters,
        campaignId,
        page: recordsPage,
        pageSize: recordsPageSize,
      });
      setRecords(data.items || []);
      setRecordsTotal(data.total || 0);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '获取抽奖记录失败');
    } finally {
      setRecordLoading(false);
    }
  }, [campaignId, hasValidCampaignId, message, recordFilters, recordsPage, recordsPageSize]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchCampaign(), fetchPrizes(), fetchRecords()]);
  }, [fetchCampaign, fetchPrizes, fetchRecords]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const handleSaveCampaign = async () => {
    if (!campaign) return;

    try {
      const values = await campaignForm.validateFields();
      await updateLotteryCampaign(toCampaignPayload(values), campaign.id);
      message.success('活动配置已保存');
      await fetchCampaign();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '保存活动失败');
    }
  };

  const openPrizeModal = (prize?: LotteryPrize) => {
    setEditingPrize(prize || null);
    prizeForm.setFieldsValue(
      prize
        ? {
            name: prize.name,
            description: prize.description || '',
            isNoPrize: prize.isNoPrize,
            weight: prize.weight,
            stock: prize.stock,
            remainingStock: prize.remainingStock,
            sortOrder: prize.sortOrder,
            isActive: prize.isActive,
          }
        : {
            name: '',
            description: '',
            isNoPrize: false,
            weight: 10,
            stock: -1,
            remainingStock: -1,
            sortOrder: prizes.length + 1,
            isActive: true,
          },
    );
    setPrizeModalVisible(true);
  };

  const handleSavePrize = async () => {
    if (!campaign) return;

    try {
      const values = await prizeForm.validateFields();
      if (editingPrize) {
        await updateLotteryPrize(editingPrize.id, values);
        message.success('奖品已更新');
      } else {
        const { remainingStock, ...createValues } = values;
        await createLotteryPrize(createValues, campaign.id);
        message.success('奖品已创建');
      }
      setPrizeModalVisible(false);
      await Promise.all([fetchPrizes(), fetchCampaign()]);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '保存奖品失败');
    }
  };

  const handleDeletePrize = async (prize: LotteryPrize) => {
    try {
      const result = await deleteLotteryPrize(prize.id);
      message.success(result.disabled ? '奖品已有抽奖记录，已自动停用' : '奖品已删除');
      await Promise.all([fetchPrizes(), fetchCampaign()]);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '删除奖品失败');
    }
  };

  const applyRecordFilters = () => {
    setRecordsPage(1);
    setRecordFilters({
      phone: draftFilters.phone?.trim() || undefined,
      orderNo: draftFilters.orderNo?.trim() || undefined,
      prizeId: draftFilters.prizeId,
      fulfillmentStatus: draftFilters.fulfillmentStatus,
    });
  };

  const resetRecordFilters = () => {
    setDraftFilters({});
    setRecordFilters({});
    setRecordsPage(1);
  };

  const openFulfillModal = (record: LotteryRecord) => {
    setFulfillingRecord(record);
    fulfillForm.setFieldsValue({
      fulfillmentNote: record.fulfillmentNote || '',
    });
    setFulfillModalVisible(true);
  };

  const handleFulfillRecord = async () => {
    if (!fulfillingRecord) return;

    try {
      const values = await fulfillForm.validateFields();
      await updateLotteryRecordStatus(fulfillingRecord.id, {
        fulfillmentStatus: 'fulfilled',
        fulfillmentNote: values.fulfillmentNote || null,
      });
      message.success('已标记兑奖');
      setFulfillModalVisible(false);
      await Promise.all([fetchRecords(), fetchCampaign()]);
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || '更新兑奖状态失败');
    }
  };

  const prizeColumns = createPrizeColumns(openPrizeModal, handleDeletePrize);
  const recordColumns = createRecordColumns(openFulfillModal);

  const stats = getStats(campaign);
  const soldOutPrizes = prizes.filter((prize) => prize.isActive && prize.stock !== -1 && prize.remainingStock <= 0);
  const lowStockPrizes = prizes.filter(
    (prize) => prize.isActive && prize.stock !== -1 && prize.remainingStock > 0 && prize.remainingStock <= 5,
  );
  const runtimeStatus = campaign ? getRuntimeStatus(campaign) : null;

  const overviewTab = (
    <div className="lottery-tab-panel">
      <Row gutter={[12, 12]}>
        <Col xs={12} lg={6}>
          <div className="lottery-stat-card">
            <Statistic title="抽奖次数" value={stats.drawCount} />
          </div>
        </Col>
        <Col xs={12} lg={6}>
          <div className="lottery-stat-card">
            <Statistic title="中奖次数" value={stats.winningCount} />
          </div>
        </Col>
        <Col xs={12} lg={6}>
          <div className="lottery-stat-card">
            <Statistic title="待兑奖" value={stats.pendingFulfillmentCount} valueStyle={{ color: '#d46b08' }} />
          </div>
        </Col>
        <Col xs={12} lg={6}>
          <div className="lottery-stat-card">
            <Statistic title="启用奖品" value={stats.activePrizeCount} suffix={`/ ${stats.prizeCount}`} />
          </div>
        </Col>
      </Row>

      {soldOutPrizes.length > 0 && (
        <Alert
          className="lottery-alert"
          type="warning"
          showIcon
          message={`有 ${soldOutPrizes.length} 个奖品库存已空`}
          description={soldOutPrizes.map((prize) => prize.name).join('、')}
        />
      )}
      {lowStockPrizes.length > 0 && (
        <Alert
          className="lottery-alert"
          type="info"
          showIcon
          message={`有 ${lowStockPrizes.length} 个奖品库存偏低`}
          description={lowStockPrizes.map((prize) => `${prize.name}(${prize.remainingStock})`).join('、')}
        />
      )}

      <Descriptions bordered size="small" column={2} className="lottery-descriptions">
        <Descriptions.Item label="活动名称">{campaign?.name || '-'}</Descriptions.Item>
        <Descriptions.Item label="运行状态">
          {runtimeStatus ? <Tag color={runtimeStatus.color}>{runtimeStatus.label}</Tag> : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="客户端主活动">
          {campaign?.isPrimary ? <Tag color="blue">是</Tag> : <Tag>否</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="抽奖资格">
          {campaign?.isStandalone ? <Tag color="purple">独立资格</Tag> : <Tag>共享历史状态</Tag>}
        </Descriptions.Item>
        <Descriptions.Item label="活动时间" span={2}>{formatCampaignTime(campaign)}</Descriptions.Item>
        <Descriptions.Item label="活动说明" span={2}>{campaign?.description || '-'}</Descriptions.Item>
        <Descriptions.Item label="活动规则" span={2}>{campaign?.rules || '-'}</Descriptions.Item>
      </Descriptions>
    </div>
  );

  const configTab = (
    <div className="lottery-tab-panel">
      <Form form={campaignForm} layout="vertical" className="lottery-campaign-form-detail">
        <Form.Item name="name" label="活动名称" rules={[{ required: true, message: '请输入活动名称' }]}>
          <Input placeholder="办卡成功抽奖" />
        </Form.Item>
        <Form.Item name="isActive" label="活动开关" valuePropName="checked">
          <Switch checkedChildren="启用" unCheckedChildren="停用" />
        </Form.Item>
        <Form.Item name="isPrimary" label="客户端主活动" valuePropName="checked">
          <Switch checkedChildren="是" unCheckedChildren="否" />
        </Form.Item>
        <Form.Item
          name="isStandalone"
          label="独立抽奖资格"
          valuePropName="checked"
          extra="默认关闭。关闭时，一个订单在任意活动抽过就不能再抽；开启时，本活动不受其他活动已抽状态影响。"
        >
          <Switch checkedChildren="独立" unCheckedChildren="共享" />
        </Form.Item>
        <Form.Item name="dateRange" label="活动时间">
          <RangePicker showTime style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="description" label="活动说明">
          <Input placeholder="展示给用户看的活动说明" />
        </Form.Item>
        <Form.Item name="rules" label="活动规则">
          <TextArea rows={4} placeholder="仅成功订单可参与，每个订单限抽一次。" />
        </Form.Item>
        <Button type="primary" icon={<SaveOutlined />} loading={campaignLoading} onClick={handleSaveCampaign}>
          保存基础配置
        </Button>
      </Form>
    </div>
  );

  const prizesTab = (
    <div className="lottery-tab-panel">
      <div className="lottery-section-title">
        <span>奖品配置</span>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openPrizeModal()}>
          新增奖品
        </Button>
      </div>
      <Table
        rowKey="id"
        columns={prizeColumns}
        dataSource={prizes}
        loading={prizeLoading}
        pagination={false}
        size="small"
      />
    </div>
  );

  const recordsTab = (
    <div className="lottery-tab-panel">
      <div className="lottery-record-toolbar">
        <Input
          placeholder="手机号"
          allowClear
          value={draftFilters.phone}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, phone: event.target.value }))}
          style={{ width: 140 }}
        />
        <Input
          placeholder="订单号"
          allowClear
          value={draftFilters.orderNo}
          onChange={(event) => setDraftFilters((prev) => ({ ...prev, orderNo: event.target.value }))}
          style={{ width: 170 }}
        />
        <Select
          placeholder="奖品"
          allowClear
          value={draftFilters.prizeId}
          onChange={(value) => setDraftFilters((prev) => ({ ...prev, prizeId: value }))}
          style={{ width: 180 }}
          options={prizes.map((prize) => ({ label: prize.name, value: prize.id }))}
        />
        <Select
          placeholder="兑奖状态"
          allowClear
          value={draftFilters.fulfillmentStatus}
          onChange={(value) => setDraftFilters((prev) => ({ ...prev, fulfillmentStatus: value }))}
          style={{ width: 130 }}
          options={[
            { label: '待兑奖', value: 'pending' },
            { label: '已兑奖', value: 'fulfilled' },
            { label: '无需兑奖', value: 'none' },
          ]}
        />
        <Button type="primary" onClick={applyRecordFilters}>筛选</Button>
        <Button onClick={resetRecordFilters}>重置</Button>
      </div>
      <Table
        rowKey="id"
        columns={recordColumns}
        dataSource={records}
        loading={recordLoading}
        size="small"
        pagination={{
          current: recordsPage,
          pageSize: recordsPageSize,
          total: recordsTotal,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setRecordsPage(nextPage);
            setRecordsPageSize(nextPageSize);
          },
        }}
      />
    </div>
  );

  const tabItems = [
    { key: 'overview', label: '概览', children: overviewTab },
    { key: 'config', label: '基础配置', children: configTab },
    { key: 'prizes', label: '奖品配置', children: prizesTab },
    { key: 'records', label: '中奖记录', children: recordsTab },
  ];

  return (
    <div className="lottery-page">
      <div className="lottery-header">
        <div>
          <Space align="center" className="lottery-breadcrumb-action">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/lottery')}>
              返回活动列表
            </Button>
          </Space>
          <Title level={4}>{campaign?.name || '抽奖活动详情'}</Title>
          <Space wrap>
            {runtimeStatus && <Tag color={runtimeStatus.color}>{runtimeStatus.label}</Tag>}
            {campaign?.isPrimary && <Tag color="blue">客户端主活动</Tag>}
            {campaign?.isStandalone ? <Tag color="purple">独立资格</Tag> : <Tag>共享资格</Tag>}
          </Space>
        </div>
        <Button icon={<ReloadOutlined />} loading={campaignLoading || prizeLoading || recordLoading} onClick={refreshAll}>
          刷新
        </Button>
      </div>

      <div className="lottery-panel">
        {campaign ? (
          <Tabs defaultActiveKey="overview" items={tabItems} />
        ) : (
          <Empty description="未找到活动" />
        )}
      </div>

      <PrizeModal
        open={prizeModalVisible}
        editingPrize={editingPrize}
        form={prizeForm}
        onCancel={() => setPrizeModalVisible(false)}
        onOk={handleSavePrize}
      />

      <FulfillModal
        open={fulfillModalVisible}
        record={fulfillingRecord}
        form={fulfillForm}
        onCancel={() => setFulfillModalVisible(false)}
        onOk={handleFulfillRecord}
      />
    </div>
  );
};

export default LotteryDetailPage;
